import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import MainLayout from '../components/MainLayout';
import { useInventoryStore, GroupedInventoryItem } from '../store/inventoryStore';
import usePageTitle from '../utils/usePageTitle';
import { useGlobalLoading } from '../hooks/useGlobalLoading';
import Loader from '../components/Loader';
import ConfirmModal from '../components/ConfirmModal';
import { useApiCall } from '../hooks/useApiCall';
import SalesTable from '../components/SalesTable';
import SalesFilters from '../components/SalesFilters';

// Стилизованные компоненты
const PageContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 80px);
  box-sizing: border-box;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 24px;
  color: #FFFFFF;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
  
  @media (min-width: 1024px) {
    font-size: 32px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 12px;
  border: 2px solid #609F94;
  border-radius: 8px;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    padding: 7px 14px;
    margin-bottom: 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 8px 16px;
    margin-bottom: 32px;
  }
  
  @media (min-width: 1440px) {
    padding: 10px 20px;
    margin-bottom: 40px;
  }
  
  @media (min-width: 1920px) {
    padding: 12px 24px;
    margin-bottom: 48px;
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Ubuntu', sans-serif;
  font-size: 12px;
  color: #FFFFFF;
  
  @media (min-width: 768px) {
    font-size: 13px;
  }
  
  @media (min-width: 1024px) {
    font-size: 14px;
  }
  
  @media (min-width: 1440px) {
    font-size: 16px;
  }
  
  @media (min-width: 1920px) {
    font-size: 18px;
  }
  
  &::placeholder {
    color: #609F94;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 8px;
  flex: 1;
  overflow: visible;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
`;

// Контейнер для карточки с кнопкой раскрытия (адаптивный)
const CardWithExpandWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 480px) {
    gap: 10px;
  }
  
  @media (min-width: 768px) {
    gap: 12px;
  }
  
  @media (min-width: 1024px) {
    gap: 16px;
  }
  
  @media (min-width: 1440px) {
    gap: 20px;
  }
`;

const FilterRow = styled.div`
  display: none;
  user-select: none;
  
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 320px 160px 130px 160px;
    align-items: center;
    width: 100%;
    margin-bottom: 8px;
    column-gap: 16px;
    padding: 0 16px;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 380px 180px 150px 180px;
    column-gap: 20px;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: 440px 200px 170px 200px;
    column-gap: 24px;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: 500px 220px 190px 220px;
    column-gap: 32px;
    padding: 0 24px;
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 100%;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  user-select: none;
  
  
  &:first-child {
    justify-content: flex-start;
    cursor: default;
    
    &:hover {
      background-color: transparent;
    }
  }
  
  &:last-child {
    cursor: default;
    
    &:hover {
      background-color: transparent;
    }
  }
`;

const FilterLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609F94;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;
  
  @media (min-width: 768px) {
    font-size: 13px;
  }
  
  @media (min-width: 1024px) {
    font-size: 14px;
  }
  
  @media (min-width: 1440px) {
    font-size: 16px;
  }
  
  @media (min-width: 1920px) {
    font-size: 18px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 0 12px;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    padding: 0 16px;
  }
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.16)' : 'rgba(8, 28, 33, 0.48)'};
  border: 1px solid ${props => props.$active ? '#24BA7D' : 'rgba(255, 255, 255, 0.16)'};
  color: ${props => props.$active ? '#24BA7D' : '#FFFFFF'};
  font-family: 'Ubuntu', sans-serif;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.24)' : 'rgba(8, 28, 33, 0.64)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609F94;
  margin-right: 0;
  text-align: center;
  flex: 1;
  min-width: 200px;
  
  @media (min-width: 768px) {
    font-size: 14px;
    margin-right: auto;
    flex: none;
    min-width: auto;
  }
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0;
  
  @media (min-width: 768px) {
    margin-left: 16px;
  }
`;

const ItemsPerPageLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  margin-right: 8px;
`;

const ItemsPerPageSelect = styled.select`
  background-color: rgba(8, 28, 33, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  padding: 4px 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(36, 186, 125, 0.48);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  option {
    background-color: #172A2A;
  }
`;

// Функция для группировки размеров
const groupSizesByValue = (sizes: any[]) => {
  const groupedSizes = new Map<string, { size: string; count: number; firstItem: any }>();
  
  sizes.forEach(size => {
    const sizeValue = size.size.replace(' US', '');
    if (groupedSizes.has(sizeValue)) {
      const existing = groupedSizes.get(sizeValue)!;
      existing.count += 1;
    } else {
      groupedSizes.set(sizeValue, {
        size: sizeValue,
        count: 1,
        firstItem: size
      });
    }
  });

  return Array.from(groupedSizes.values()).sort((a, b) => {
    const aNum = parseFloat(a.size);
    const bNum = parseFloat(b.size);
    return aNum - bNum;
  });
};

// Функции форматирования
const formatPrice = (price: string): string => {
  if (!price) return '—';
  
  const cleanPrice = price.replace(/[^\d\-\s]/g, '');
  
  if (cleanPrice.includes('-')) {
    const parts = cleanPrice.split('-').map(p => p.trim());
    if (parts.length === 2 && parts[0] && parts[1]) {
      const min = parseInt(parts[0]).toLocaleString('ru-RU');
      const max = parseInt(parts[1]).toLocaleString('ru-RU');
      return `${min} - ${max} ₽`;
    }
  }
  
  const numericPrice = parseInt(cleanPrice.replace(/\s/g, ''));
  if (!isNaN(numericPrice)) {
    return `${numericPrice.toLocaleString('ru-RU')} ₽`;
  }
  
  return price;
};

const formatMarketPrice = (price: string | number | undefined): string => {
  if (!price || price === '—' || price === '-') return '—';
  
  const priceStr = typeof price === 'number' ? price.toString() : price;
  const cleanPrice = priceStr.replace(/[^\d.,]/g, '');
  
  if (!cleanPrice) return '—';
  
  const numericPrice = parseFloat(cleanPrice.replace(',', '.'));
  
  if (isNaN(numericPrice)) return '—';
  
  return `${numericPrice} ₽`;
};

// Иконки
const SortIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  flex-shrink: 0;
  user-select: none;
  
  svg {
    width: 20px;
    height: 20px;
    
    @media (min-width: 768px) {
      width: 22px;
      height: 22px;
    }
    
    @media (min-width: 1024px) {
      width: 24px;
      height: 24px;
    }
    
    @media (min-width: 1440px) {
      width: 26px;
      height: 26px;
    }
    
    @media (min-width: 1920px) {
      width: 28px;
      height: 28px;
    }
  }
`;

const SortIcon = ({ direction }: { direction?: 'up' | 'down' | null }) => (
  direction && (
    <SortIconWrapper>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d={direction === 'up'
            ? "M7.54 6.1L10 3.64L12.46 6.1"
            : "M12.46 13.88L10 16.34L7.54 13.88"}
          stroke="#609F94"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </SortIconWrapper>
  )
);

const ArrowDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.6 7.45834L11.1667 12.8917C10.525 13.5333 9.47497 13.5333 8.83331 12.8917L3.39998 7.45834" stroke="#A3A3A3" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeftDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.2 12.4333L3.96667 9.2C3.43334 8.66667 3.43334 7.78 3.96667 7.24667L7.2 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.0333 12.4333L8.79999 9.2C8.26666 8.66667 8.26666 7.78 8.79999 7.24667L12.0333 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.96667 12.4333L12.2 9.2C12.7333 8.66667 12.7333 7.78 12.2 7.24667L8.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.96667 12.4333L7.20001 9.2C7.73334 8.66667 7.73334 7.78 7.20001 7.24667L3.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.0334 4.01334L6.80002 7.24667C6.26669 7.78 6.26669 8.66667 6.80002 9.2L10.0334 12.4333" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.96667 12.4333L9.20001 9.2C9.73334 8.66667 9.73334 7.78 9.20001 7.24667L5.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Компонент страницы продаж
const Sales: React.FC = () => {
  usePageTitle('Продажи');

  const {
    groupedSalesItems,
    isLoading,
    fetchSales,
    searchSales,
    salesSearchResults,
    isSearching,
    clearSearchResults,
    deleteSneaker,
    searchCurrentPage,
    searchTotalItems,
    searchItemsPerPage,
    setSearchPage,
    setSearchItemsPerPage,
    salesCurrentPage,
    salesTotalItems,
    salesItemsPerPage,
    setSalesPage,
    setSalesItemsPerPage
  } = useInventoryStore();

  const { addLoadingStep, removeLoadingStep } = useGlobalLoading();
  const { executeApiCall } = useApiCall();

  // Состояния
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCards, setExpandedCards] = useState<string[]>([]);
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  // Состояние для модального окна подтверждения
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { }
  });

  // Дебаунс для поиска
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        searchSales(searchQuery);
      } else {
        // Очищаем только результаты поиска при пустом запросе
        clearSearchResults();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchSales, clearSearchResults]);

  // Фильтрация по поиску - используем результаты API
  const filteredItems = searchQuery.trim() ? salesSearchResults : groupedSalesItems;

  // Для серверной пагинации не используем локальную сортировку
  // Данные уже приходят отсортированными с сервера
  const currentItems = filteredItems;

  const isSearchMode = searchQuery.trim() !== '';
  const currentPageToUse = isSearchMode ? searchCurrentPage + 1 : salesCurrentPage + 1;
  const totalItemsToUse = isSearchMode ? searchTotalItems : salesTotalItems;
  const itemsPerPageToUse = isSearchMode ? searchItemsPerPage : salesItemsPerPage;
  const totalPages = Math.ceil(totalItemsToUse / itemsPerPageToUse);

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    if (groupedSalesItems.length === 0) {
      fetchSales(0, salesItemsPerPage);
    }
  }, []);

  // Управление глобальной загрузкой
  useEffect(() => {
    if (isLoading && groupedSalesItems.length === 0) {
      addLoadingStep('sales-loading', 'Загружаем проданные товары...', 5);
    } else {
      removeLoadingStep('sales-loading');
    }
  }, [isLoading, groupedSalesItems.length, addLoadingStep, removeLoadingStep]);

  // Обработчики
  const handlePageChange = (pageNumber: number) => {
    const isSearchMode = searchQuery.trim() !== '';
    
    if (isSearchMode) {
      setSearchPage(pageNumber - 1, true); // API использует 0-based индекс
    } else {
      setSalesPage(pageNumber - 1); // API использует 0-based индекс
    }
    setExpandedCards([]);
  };

  // Обработчик изменения количества элементов на странице
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = Number(e.target.value);
    const isSearchMode = searchQuery.trim() !== '';
    
    if (isSearchMode) {
      setSearchItemsPerPage(newItemsPerPage, true);
    } else {
      setSalesItemsPerPage(newItemsPerPage);
    }
    setExpandedCards([]);
  };

  // Обработчик удаления товара
  const handleDelete = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Удаление товара',
      message: 'Вы уверены, что хотите удалить этот товар? Это действие нельзя отменить.',
      onConfirm: async () => {
        await executeApiCall(
          () => deleteSneaker(id),
          { successMessage: 'Товар успешно удален' }
        );
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const toggleExpand = (article: string) => {
    setExpandedCards(prev =>
      prev.includes(article)
        ? prev.filter(cardArticle => cardArticle !== article)
        : [...prev, article]
    );
  };

  const toggleSort = (field: 'size' | 'price') => {
    setSorting(prev => {
      const existingSort = prev.find(s => s.id === field);
      if (!existingSort) {
        return [{ id: field, desc: false }];
      } else if (!existingSort.desc) {
        return [{ id: field, desc: true }];
      } else {
        return [];
      }
    });
  };

  const getSortDirection = (field: string): 'up' | 'down' | null => {
    const sortState = sorting.find(s => s.id === field);
    if (!sortState) return null;
    return sortState.desc ? 'down' : 'up';
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    if (currentPageToUse > 1) {
      buttons.push(
        <PageButton
          key="first"
          onClick={() => handlePageChange(1)}
          disabled={currentPageToUse === 1 || isLoading || isSearching}
        >
          <ArrowLeftDoubleIcon />
        </PageButton>
      );
    }

    if (currentPageToUse > 1) {
      buttons.push(
        <PageButton
          key="prev"
          onClick={() => handlePageChange(currentPageToUse - 1)}
          disabled={currentPageToUse === 1 || isLoading || isSearching}
        >
          <ArrowLeftIcon />
        </PageButton>
      );
    }

    let startPage = Math.max(1, currentPageToUse - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <PageButton
          key={i}
          $active={i === currentPageToUse}
          onClick={() => handlePageChange(i)}
          disabled={isLoading || isSearching}
        >
          {i}
        </PageButton>
      );
    }

    if (currentPageToUse < totalPages) {
      buttons.push(
        <PageButton
          key="next"
          onClick={() => handlePageChange(currentPageToUse + 1)}
          disabled={currentPageToUse === totalPages || isLoading || isSearching}
        >
          <ArrowRightIcon />
        </PageButton>
      );
    }

    if (currentPageToUse < totalPages) {
      buttons.push(
        <PageButton
          key="last"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPageToUse === totalPages || isLoading || isSearching}
        >
          <ArrowRightDoubleIcon />
        </PageButton>
      );
    }

    return buttons;
  };

  if (isLoading && !groupedSalesItems.length) {
    return (
      <MainLayout activePage="sales">
        <div />
      </MainLayout>
    );
  }

  return (
    <MainLayout activePage="sales">
      <PageContainer>
        <HeaderContainer data-tutorial="sales-header">
          <PageTitle>Продажи</PageTitle>
        </HeaderContainer>

        <SearchContainer data-tutorial="sales-search">
          <SearchInput
            placeholder="Найти проданный товар"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {isSearching && <div style={{ marginLeft: '10px', fontSize: '14px', color: '#609F94' }}>Поиск...</div>}
        </SearchContainer>

        <SalesFilters />

        <div data-tutorial="sales-list">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <Loader size="medium" text="Загружаем страницу..." />
            </div>
          ) : (
            <SalesTable
              data={currentItems}
              isLoading={isLoading}
              onDelete={handleDelete}
              formatPrice={formatPrice}
              formatMarketPrice={formatMarketPrice}
            />
          )}
        </div>

        {currentItems.length > 0 && totalPages > 1 && (
          <PaginationContainer>
            <PaginationInfo>
              {currentItems.length === 0
                ? 'Нет элементов'
                : `Показано ${((currentPageToUse - 1) * itemsPerPageToUse) + 1}-${Math.min(currentPageToUse * itemsPerPageToUse, totalItemsToUse)} из ${totalItemsToUse}`
              }
            </PaginationInfo>
            {renderPaginationButtons()}
            <ItemsPerPageContainer>
              <ItemsPerPageLabel>Показывать по:</ItemsPerPageLabel>
              <ItemsPerPageSelect
                value={itemsPerPageToUse}
                onChange={handleItemsPerPageChange}
                disabled={isSearching || isLoading}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                {!isSearchMode && <option value={salesTotalItems}>Все</option>}
              </ItemsPerPageSelect>
            </ItemsPerPageContainer>
          </PaginationContainer>
        )}
      </PageContainer>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </MainLayout>
  );
};

export default Sales;